import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { MapPin, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

const Contact = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    category: "",
    message: "",
  });

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(t("contact.successMsg"));
    setFormData({
      name: "",
      email: "",
      phone: "",
      category: "",
      message: "",
    });
  };

  return (
    <div className="min-h-screen bg-[#FBFBFB] flex flex-col">
      {/* Banner */}
      <div
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1920&q=80')",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <h1
          data-aos="fade-up"
          className="relative text-2xl md:text-4xl font-bold font-philosopher text-white z-10"
        >
          {t("contact.title")}
        </h1>
      </div>

      {/* Contact Info */}
      <section className="py-10 px-4 md:px-10 bg-white">
        <div className="w-full mx-auto text-center">
          <h2
            data-aos="fade-up"
            className="text-3xl font-philosopher text-gray-800 mb-10"
          >
            {t("contact.getInTouch")}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Location */}
            <div
              data-aos="fade-up"
              data-aos-delay="100"
              className="bg-gray-100 p-8 rounded-2xl shadow hover:shadow-lg transition"
            >
              <div className="flex justify-center mb-4">
                <MapPin className="w-10 h-10 text-indigo-600" />
              </div>

              <h3 className="text-xl font-philosopher font-bold mb-2">
                {t("contact.locationTitle")}
              </h3>

              <p className="text-gray-600">
                HIG S 117 MAITRY VIHAR SERLY PROJ COMPLEX,KHORDHA , BHUBANESWAR,
                ODISHA, INDIA, 751023
              </p>
            </div>

            {/* Email */}
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="bg-gray-100 p-8 rounded-2xl shadow hover:shadow-lg transition"
            >
              <div className="flex justify-center mb-4">
                <Mail className="w-10 h-10 text-indigo-600" />
              </div>

              <h3 className="text-xl font-philosopher font-bold mb-2">
                {t("contact.emailTitle")}
              </h3>

              <p className="text-gray-600">support@bharatkarigari.com</p>
            </div>

            {/* Phone */}
            <div
              data-aos="fade-up"
              data-aos-delay="300"
              className="bg-gray-100 p-8 rounded-2xl shadow hover:shadow-lg transition"
            >
              <div className="flex justify-center mb-4">
                <Phone className="w-10 h-10 text-indigo-600" />
              </div>

              <h3 className="text-xl font-philosopher font-bold mb-2">
                {t("contact.phoneTitle")}
              </h3>

              <p className="text-gray-600">+91 7735172177</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-10 px-4 md:px-10 bg-gradient-to-b from-indigo-50 to-white">
        <div className="bg-white p-10 rounded-3xl shadow-lg">
          <h2
            data-aos="fade-up"
            className="text-3xl font-philosopher text-gray-800 text-center mb-10"
          >
            {t("contact.sendMessage")}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name & Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div data-aos="fade-up">
                <label className="block text-gray-700 font-medium mb-2">
                  {t("contact.fullName")}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t("contact.enterName")}
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div data-aos="fade-up">
                <label className="block text-gray-700 font-medium mb-2">
                  {t("contact.emailAddress")}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t("contact.enterEmail")}
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Phone & Category */}
            <div className="grid md:grid-cols-2 gap-6">
              <div data-aos="fade-up">
                <label className="block text-gray-700 font-medium mb-2">
                  {t("contact.phoneNumber")}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t("contact.enterPhone")}
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div data-aos="fade-up">
                <label className="block text-gray-700 font-medium mb-2">
                  {t("contact.category")}
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="">{t("contact.selectIssue")}</option>
                  <option value="order">{t("contact.orderRelated")}</option>
                  <option value="payment">{t("contact.paymentIssue")}</option>
                  <option value="shipping">{t("contact.shippingDelay")}</option>
                  <option value="other">{t("contact.other")}</option>
                </select>
              </div>
            </div>

            {/* Message */}
            <div data-aos="fade-up">
              <label className="block text-gray-700 font-medium mb-2">
                {t("contact.yourMessage")}
              </label>

              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder={t("contact.writeMessage")}
                rows="5"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              ></textarea>
            </div>

            {/* Submit */}
            <div data-aos="zoom-in" className="text-center pt-4">
              <button
                type="submit"
                className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-md hover:bg-indigo-700 transition transform hover:-translate-y-1"
              >
                {t("contact.sendBtn")}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Map */}
      <section data-aos="fade-up" className="pb-10 px-4 md:px-10">
        <div className="w-full mx-auto">
          <iframe
            title="Our Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7483.451482789106!2d85.80830439135032!3d20.31161617465676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a1909b3d62e4f53%3A0x2bd1fc3ae1736699!2sMaitri%20Vihar%2C%20Chandrasekharpur%2C%20Bhubaneswar%2C%20Odisha!5e0!3m2!1sen!2sin!4v1790147728622!5m2!1sen!2sin"
            className="w-full h-[400px] rounded-2xl shadow-lg border-0"
            loading="lazy"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;
